'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  Delete02Icon,
  Link02Icon,
  SourceCodeIcon,
  TerminalIcon,
} from '@terra/ui-icons';
import type { UnverifiedAssetDetailDto } from '@terra/shared-contracts-assets';
import { Badge } from '../../../components/badge';
import { BadgeHttp } from '../../../components/styled/badge-http';
import { IconButton } from '../../../components/icon-button';
import { Tooltip } from '../../../components/tooltip';
import { MetaDefault, MonoDefault } from '../../../components/typography';
import { CopyIdButton } from '../../copy-id-button/CopyIdButton';
import { UnverifiedAssetBadge } from '../UnverifiedAssetBadge';
import { UnverifiedAssetRerunControls } from './UnverifiedAssetRerunControls';
import { resolveUnverifiedAssetOriginLabelKey } from './unverified-asset-origin.utils';

type UnverifiedAssetDetailHeaderProps = {
  detail: UnverifiedAssetDetailDto;
  onDelete?: () => void;
  onRerun?: () => void;
  onRerunWithContext?: () => void;
  isDeleting?: boolean;
  isRerunning?: boolean;
};

export function UnverifiedAssetDetailHeader({
  detail,
  onDelete,
  onRerun,
  onRerunWithContext,
  isDeleting = false,
  isRerunning = false,
}: UnverifiedAssetDetailHeaderProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const tDrawer = useTranslations('designSystem.signals.drawer');
  const originLabel = t(
    resolveUnverifiedAssetOriginLabelKey({ originKind: detail.origin.kind }),
  );
  const showSourceCodeIcon = detail.origin.kind === 'SOURCE_CODE';

  return (
    <VStack
      gap="2"
      alignItems="flex-start"
      w="full"
      p="4"
      borderBottomWidth="1px"
      borderColor="border.subtle"
    >
      <HStack gap="3" alignItems="center" w="full">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="8"
          h="8"
          flexShrink={0}
          borderRadius="md"
          bg="bg.neutral.error"
          color="fg.neutral.error"
        >
          <TerminalIcon size={16} />
        </Box>
        <BadgeHttp method={detail.request.method} size="sm">
          {detail.request.method}
        </BadgeHttp>
        <HStack gap="2" alignItems="center" minW={0} flexShrink={1}>
          <MonoDefault
            color="fg.default"
            fontWeight="medium"
            minW={0}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {detail.path}
          </MonoDefault>
          <UnverifiedAssetBadge unverifiedStatus={detail.unverifiedStatus} />
        </HStack>
        <Box flex="1" minW={0} />
        <CopyIdButton id={detail.id} entityName={tDrawer('asset')} />
        {onDelete ? (
          <Tooltip title={t('actions.delete')}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={t('actions.delete')}
              disabled={isDeleting || isRerunning}
              onClick={onDelete}
            >
              <Delete02Icon size={14} />
            </IconButton>
          </Tooltip>
        ) : null}
        <UnverifiedAssetRerunControls
          onRerun={onRerun}
          onRerunWithContext={onRerunWithContext}
          isRerunning={isRerunning}
        />
      </HStack>

      <HStack gap="1" alignItems="center" w="full" minW={0}>
        <Box display="flex" alignItems="center" aria-hidden flexShrink={0}>
          <Link02Icon size={14} />
        </Box>
        <MonoDefault
          color="fg.muted"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {detail.url}
        </MonoDefault>
      </HStack>

      <HStack gap="2" alignItems="center" w="full" flexWrap="wrap">
        <MetaDefault color="fg.muted">{t('origin')}</MetaDefault>
        <Tooltip title={detail.origin.sourceDetail ?? originLabel}>
          <Badge size="sm" variant="outline" w="fit-content" minW="auto">
            <HStack gap="1" alignItems="center">
              {showSourceCodeIcon ? <SourceCodeIcon size={12} /> : null}
              <span>{originLabel}</span>
            </HStack>
          </Badge>
        </Tooltip>
      </HStack>
    </VStack>
  );
}
